import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Folder as FolderIcon, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useProjectFolderTree, type FolderTreeNode } from "@/hooks/use-folders";

/**
 * Recursive folder tree for the project sidebar (Task 6). Fetches the whole
 * project's folders flat (one request) and nests them by parentFolderId. A row
 * click navigates into the folder-detail page; the chevron expands/collapses.
 */
export function FolderTree({
  projectId,
  foldersBasePath,
}: {
  projectId: string | undefined;
  foldersBasePath: string; // e.g. /admin/projects/:id/folders
}) {
  const { data: nodes, isLoading } = useProjectFolderTree(projectId);

  // Group children by parent for O(1) lookup while rendering.
  const childrenOf = useMemo(() => {
    const map = new Map<string | null, FolderTreeNode[]>();
    for (const n of nodes ?? []) {
      const key = n.parentFolderId ?? null;
      const arr = map.get(key) ?? [];
      arr.push(n);
      map.set(key, arr);
    }
    return map;
  }, [nodes]);

  if (isLoading) {
    return <div className="px-2 py-1 text-xs text-muted-foreground">Loading…</div>;
  }
  const roots = childrenOf.get(null) ?? [];
  if (roots.length === 0) {
    return <div className="px-2 py-1 text-xs text-muted-foreground">No folders</div>;
  }

  return (
    <ul className="space-y-0.5">
      {roots.map((n) => (
        <TreeNode
          key={n.id}
          node={n}
          depth={0}
          childrenOf={childrenOf}
          foldersBasePath={foldersBasePath}
        />
      ))}
    </ul>
  );
}

function TreeNode({
  node,
  depth,
  childrenOf,
  foldersBasePath,
}: {
  node: FolderTreeNode;
  depth: number;
  childrenOf: Map<string | null, FolderTreeNode[]>;
  foldersBasePath: string;
}) {
  const { folderId: activeFolderId } = useParams<{ folderId: string }>();
  const kids = childrenOf.get(node.id) ?? [];
  const hasKids = kids.length > 0;
  // Auto-expand the branch containing the active folder.
  const [open, setOpen] = useState(false);
  const active = activeFolderId === node.id;

  return (
    <li>
      <div
        className={cn(
          "group flex items-center gap-1 rounded-md pr-1.5 text-sm transition-colors",
          active ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
        )}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        <button
          type="button"
          aria-label={hasKids ? (open ? "Collapse" : "Expand") : undefined}
          onClick={() => hasKids && setOpen((v) => !v)}
          className={cn(
            "grid h-5 w-5 shrink-0 place-items-center rounded transition-transform",
            hasKids ? "hover:bg-secondary" : "invisible",
            open && "rotate-90",
          )}
        >
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
        <Link
          to={`${foldersBasePath}/${node.id}`}
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1"
        >
          <FolderIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
          <span className="truncate">{node.name}</span>
          {node.restricted && <Lock className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2} />}
        </Link>
      </div>

      {hasKids && open && (
        <ul className="space-y-0.5">
          {kids.map((k) => (
            <TreeNode
              key={k.id}
              node={k}
              depth={depth + 1}
              childrenOf={childrenOf}
              foldersBasePath={foldersBasePath}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
